import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiTrash2, FiImage, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/api';

const AdminGallery = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Events');
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery/get_all.php`);
            const data = await response.json();
            if (data.status === 'success') {
                setImages(data.data);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
            toast.error('Failed to load gallery images');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        handleFile(file);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFile = (file) => {
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            toast.error('Please select a valid image file');
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            toast.error("Please select a file");
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('title', title);
        formData.append('description', description);
        formData.append('category', category);

        try {
            const response = await fetch(`${API_BASE_URL}/gallery/upload.php`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (data.status === 'success') {
                toast.success('Image uploaded successfully');
                setTitle('');
                setDescription('');
                setPreview(null);
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                fetchImages();
            } else {
                toast.error(data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Error uploading image');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/gallery/delete.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id }),
            });
            const data = await response.json();

            if (data.status === 'success') {
                setImages(images.filter(img => img.id !== id));
                toast.success('Image deleted');
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Error deleting image');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Gallery Management</h1>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Upload New Image</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Drag & Drop Area */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-colors min-h-[300px] ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-400'
                            }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        {preview ? (
                            <div className="relative w-full h-full flex flex-col items-center">
                                <img src={preview} alt="Preview" className="max-h-60 object-contain rounded-lg shadow-sm" />
                                <button
                                    onClick={() => { setPreview(null); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                    className="mt-4 text-red-500 text-sm font-medium hover:text-red-700 flex items-center gap-1"
                                >
                                    <FiX /> Remove
                                </button>
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="bg-blue-50 p-4 rounded-full inline-block mb-3">
                                    <FiImage className="text-3xl text-blue-500" />
                                </div>
                                <p className="text-gray-600 font-medium">Drag & drop your image here</p>
                                <p className="text-gray-400 text-sm mt-1 mb-4">or</p>
                                <button
                                    onClick={() => fileInputRef.current.click()}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all text-sm font-medium"
                                >
                                    Browse Files
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                />
                                <p className="text-gray-400 text-xs mt-4">Supports JPG, PNG, GIF, WebP (Max 5MB)</p>
                            </div>
                        )}
                    </div>

                    {/* Metadata Form */}
                    <form onSubmit={handleUpload} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                placeholder="Enter image title"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                                <option>Events</option>
                                <option>Classes</option>
                                <option>Awards</option>
                                <option>Students</option>
                                <option>General</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all h-32 resize-none"
                                placeholder="Enter image description (optional)"
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={!preview || uploading}
                            className={`w-full py-3 rounded-lg font-bold text-white transition-all shadow-lg flex items-center justify-center gap-2 ${!preview || uploading
                                ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-blue-500/25'
                                }`}
                        >
                            {uploading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <FiUpload /> Upload Image
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>

            {/* Gallery Grid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-6">Uploaded Images ({images.length})</h2>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : images.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <FiImage className="text-4xl mx-auto mb-3 opacity-50" />
                        <p>No images uploaded yet</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        <AnimatePresence>
                            {images.map((img) => (
                                <motion.div
                                    key={img.id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    layout
                                    className="group relative bg-gray-50 rounded-lg overflow-hidden border border-gray-100 aspect-square"
                                >
                                    <img
                                        src={`${API_BASE_URL.replace('/api', '')}/${img.image_url}`}
                                        alt={img.title}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-3">
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() => handleDelete(img.id)}
                                                className="bg-red-500/90 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-sm"
                                                title="Delete Image"
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        </div>
                                        <div>
                                            <p className="text-white text-xs font-bold truncate">{img.title}</p>
                                            <span className="text-white/70 text-[10px] uppercase tracking-wider">{img.category}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminGallery;
